import {
    isSupportedLocale,
    type SupportedLocales,
} from "@app/shared/utils/locales";
import { calculatePercentage, roundNumber } from "@app/shared/utils/math";
import {
    AnswerStatus,
    QuestionProof,
    QuestionType,
    type GetSupplierQuestionnaireQuery,
} from "@generated/client/graphql";
import {
    type AnswerValue,
    type Group,
    type Question,
    type Stats,
} from "./types";

export const defaultStats: Stats = {
    answered: {
        count: 0,
        percentage: 0,
    },
    validated: {
        count: 0,
        percentage: 0,
    },
    total: {
        count: 0,
    },
};

export function makeStats(
    {
        total = 0,
        answered = 0,
        validated = 0,
    }: {
        total?: number;
        answered?: number;
        validated?: number;
    } = {
        total: 0,
        answered: 0,
        validated: 0,
    },
) {
    return {
        answered: {
            count: answered,
            percentage: roundNumber(calculatePercentage(answered, total)),
        },
        validated: {
            count: validated,
            percentage: roundNumber(calculatePercentage(validated, total)),
        },
        total: {
            count: total,
        },
    } satisfies Stats;
}

export function makeQuestionnaire(
    questionnaire: GetSupplierQuestionnaireQuery["supplierQuestionnaire2"],
) {
    if (!questionnaire) {
        return undefined;
    }
    if (!questionnaire.locale) {
        throw new Error("Locale not found");
    }
    if (!isSupportedLocale(questionnaire.locale.default)) {
        throw new Error("Default locale not supported");
    }
    if (!questionnaire.locale.availableLocales.every(isSupportedLocale))
        throw new Error("Available locales not supported");

    return {
        ...questionnaire,
        locale: {
            defaultLocale: questionnaire.locale.default,
            availableLocales: questionnaire.locale.availableLocales,
        },
        stats: makeStats(questionnaire.stats ?? {}),
    };
}

function updateHasValue<
    T extends { value?: AnswerValue | null; comment?: string },
>(update: T): boolean {
    const value = update.value;

    if (value == null) return false;
    if ("text" in value) {
        return !!value.text;
    }
    if ("num" in value) {
        return typeof value.num === "number";
    }

    if ("options" in value) {
        return !!value.options?.length;
    }

    if ("na" in value) {
        return !!value.na && !!update.comment;
    }

    return false;
}

export const answerOptimisticUpdateFn = (
    {
        prevAnswer,
        questionId,
    }: { prevAnswer: Question["answer"]; questionId: string },
    update: { value: AnswerValue } | { comment: string },
) => {
    const hasValue = updateHasValue(update);

    const status = hasValue ? AnswerStatus.Answered : AnswerStatus.Unknown;

    return {
        ...prevAnswer,
        ...update,
        status,
        questionId,
    };
};

export const filterQuestionsByDependency = <
    T extends {
        id: string;
        metadata?: {
            dependsOn?: {
                questionId: string;
                displayIfSelected: number[];
            } | null;
        } | null;
        answer?: {
            value?: {
                options?: number[];
            };
        } | null;
    },
>(
    questionsToFilter?: T[],
) => {
    if (!questionsToFilter) return [];

    return questionsToFilter.filter((question) => {
        if (!question.metadata?.dependsOn) return true;

        const parentQuestion = questionsToFilter?.find(
            (q) => q.id === question.metadata?.dependsOn?.questionId,
        );
        if (!parentQuestion?.answer) return false;

        return question.metadata?.dependsOn?.displayIfSelected?.some(
            (requiredAnswerIndex) =>
                parentQuestion.answer?.value?.options?.includes(
                    requiredAnswerIndex,
                ),
        );
    });
};

export const allowProof = (question: Question) => {
    if (
        question.type === QuestionType.SingleSelect ||
        question.type === QuestionType.MultiSelect
    ) {
        const options = question.metadata?.options ?? [];

        const selectedOptionIndices = question.answer?.value?.options ?? [];

        return selectedOptionIndices.some(
            (index) =>
                options[index]?.proof === QuestionProof.Required ||
                options[index]?.proof === QuestionProof.Allowed,
        );
    }
    return (
        question.metadata?.proof === QuestionProof.Allowed ||
        question.metadata?.proof === QuestionProof.Required
    );
};

export const isProofMissing = (question: Question) => {
    const evidenceFiles = question.answer?.evidenceFiles ?? [];

    if (
        question.type === QuestionType.SingleSelect ||
        question.type === QuestionType.MultiSelect
    ) {
        const options = question.metadata?.options ?? [];

        const selectedOptionIndices = question.answer?.value?.options ?? [];

        const selectedOptionsRequiringProof = selectedOptionIndices.filter(
            (index) => options[index]?.proof === QuestionProof.Required,
        );

        return (
            !!selectedOptionsRequiringProof.length &&
            evidenceFiles.length < selectedOptionsRequiringProof.length
        );
    }

    return (
        question.metadata?.proof === QuestionProof.Required &&
        !evidenceFiles.length
    );
};

export const isProofRequired = (question: Question) => {
    if (
        question.type === QuestionType.SingleSelect ||
        question.type === QuestionType.MultiSelect
    ) {
        const options = question.metadata?.options ?? [];

        const selectedOptionIndices = question.answer?.value?.options ?? [];

        const selectedOptionsRequiringProof = selectedOptionIndices.filter(
            (index) => options[index]?.proof === QuestionProof.Required,
        );

        return !!selectedOptionsRequiringProof.length;
    }

    return question.metadata?.proof === QuestionProof.Required;
};

export function makeGroup<T extends Group>(
    group: T,
    currentLocale: SupportedLocales,
) {
    return {
        ...group,
        name: group.name?.[currentLocale],
        shortName: group.shortName?.[currentLocale],
    };
}
